import type { IconName } from '#ui';

import { format, subDays } from 'date-fns';
import React, { useState } from 'react';

import { formatShortDateRange } from '@elara/client-core/lib/date-utils';
import {
  computePastEventsCollapse,
  isTravelDay,
  resolveItemsForEvent,
  toLocalDateKey,
} from '@elara/client-core/lib/itinerary-utils';
import { calculateTotalsByCurrency, cn, formatPrice, pluralize } from '@elara/client-core/lib/utils';
import { ItineraryItemEventModel } from '@elara/client-core/models/ItineraryItemEventModel';

import { useChatInputState, useItineraryState, useTripsState } from '#states/hooks.js';
import { Badge, Button, CostTotals, IconSizes, Loader, MaterialIcon } from '#ui';
import { isBookable } from '#views/trip/bookings/booking-grouping.js';

import { BaseItineraryBlock } from './BaseItineraryBlock.js';
import { BookTripModal } from './BookTripModal.js';
import { CustomBlock } from './CustomBlock.js';
import { FlightDepartureBlock } from './FlightDepartureBlock.js';
import { HotelCheckinBlock } from './HotelCheckinBlock.js';
import { LayoverBlock } from './LayoverBlock.js';
import { PlaceBlock } from './PlaceBlock.js';
import { ShareItineraryButton } from './ShareItineraryButton.js';

function CollapsedItineraryBuilder() {
  const tripsState = useTripsState();
  const currentTrip = tripsState.current;

  const formattedDateRange = currentTrip ? currentTrip.formattedShortDateRange : undefined;

  return (
    <button
      type="button"
      className="h-full w-full flex flex-col items-center justify-start cursor-pointer hover:bg-background-tertiary transition-colors py-6 gap-4"
      onClick={() => tripsState.toggleItineraryBuilderCollapsed()}
    >
      <MaterialIcon icon={'right_panel_open'} />
      <div className="flex items-center gap-4" style={{ writingMode: 'vertical-lr' }}>
        <span className="text-sm font-semibold tracking-wide">{currentTrip?.title}</span>
        {formattedDateRange && <span className="text-sm text-muted-foreground">{formattedDateRange}</span>}
      </div>
    </button>
  );
}

/**
 * A subtle outlined call-to-action for empty/placeholder slots in the itinerary builder
 * (e.g. "Find things to do" on a free day).
 */
function PlaceholderButton({ label, icon, onClick }: { label: string; icon: IconName; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="group w-full flex items-center justify-between gap-3 text-sm rounded-sm border border-input border-dashed px-2.5 py-2.5 box-border text-muted-foreground hover:border-transparent hover:bg-background-tertiary cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3">
        <MaterialIcon
          icon={icon}
          size={IconSizes.sm}
          className="opacity-80 group-hover:opacity-100 transition-colors"
        />
        {label}
      </div>
      <MaterialIcon icon="chevron_right" className="opacity-0 group-hover:opacity-100 transition-colors" />
    </div>
  );
}

export function ItineraryBuilderView() {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const chatInputState = useChatInputState();
  const currentTrip = tripsState.current;

  const destinationGroups = itineraryState.destinationGroups;
  const eventsByDay = itineraryState.eventsByDay;
  const hotelStaysByDay = itineraryState.hotelStaysByDay;
  const allItems = itineraryState.items;
  // Nights across the whole trip = one fewer than the number of calendar days.
  const nights = Math.max(0, itineraryState.tripDays.length - 1);
  // Use the trip's destination cover photo as the header background; brand placeholder otherwise.
  const coverImage = currentTrip?.coverImage;
  const hasCover = Boolean(coverImage);
  // Estimated trip total (cancelled items excluded), shown per currency — mirrors the bookings view.
  const totalCostsByCurrency = calculateTotalsByCurrency(allItems.filter((i) => !i.isCancelled));
  const hasTotals = Object.keys(totalCostsByCurrency).length > 0;
  // Only flights and rooms have checkouts — places/activities are never sent to checkout.
  const itemsToBookCount = allItems.filter((item) => (item.isFlight || item.isRoom) && isBookable(item)).length;

  const [scrollPosition, setScrollPosition] = useState(0);
  const [bookModalOpen, setBookModalOpen] = useState(false);
  // Past-day collapse (ELARA-740): while on an active trip, days before today are hidden behind a
  // "See past events" toggle so the current day stays the focus. Derived from the rendered day keys —
  // future/completed trips return `canCollapse: false` and show everything.
  const [showPastEvents, setShowPastEvents] = useState(false);
  const orderedDayKeys = destinationGroups.flatMap((group) => group.days.map((day) => toLocalDateKey(day.date)));
  const { canCollapse, isPastDay } = computePastEventsCollapse(orderedDayKeys);

  if (tripsState.isItineraryBuilderCollapsed) {
    return <CollapsedItineraryBuilder />;
  }

  return (
    <div
      className="@container w-full h-full flex flex-col items-center overflow-y-auto bg-background-tertiary"
      onScroll={(event) => setScrollPosition(event.currentTarget.scrollTop)}
    >
      <div className="relative flex flex-1 flex-col w-full overflow-hidden">
        {/* Cover — a landscape destination photo (or a flat tertiary fill when the trip has no cover photo)
            positioned behind the header + content. Top blur keeps the header legible over a photo; the bottom
            fades into the content area below. */}
        <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-0 aspect-[6/4] overflow-hidden">
          {hasCover ? (
            <>
              {/* Cover photo */}
              <span
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${coverImage})` }}
              />
              {/* Progressive blur band: backdrop-blur faded in by a top gradient mask (matches ImageCard). */}
              <span className="absolute inset-x-0 top-0 h-4/5 backdrop-blur-md [mask-image:linear-gradient(to_top,transparent,black)] [-webkit-mask-image:linear-gradient(to_top,transparent,black)]" />
              {/* Subtle dark scrim to keep text readable over bright images. */}
              <span className="absolute inset-x-0 top-0 h-4/5 bg-gradient-to-b from-black/20 to-transparent" />
            </>
          ) : (
            // No cover photo — a flat tertiary fill.
            <span className="absolute inset-0 bg-background-tertiary" />
          )}
          {/* Fade the bottom of the cover into the content area below. */}
          <span className="absolute inset-x-0 bottom-0 h-50 bg-gradient-to-t from-background-tertiary to-transparent" />
        </div>

        {/* Header — title + metadata over the cover (white) or on the plain panel (default text). */}
        <div className="relative z-10 flex flex-col flex-shrink-0 p-4 pb-2 md:px-6 md:pt-6">
          <div
            className={cn(
              'absolute top-6 right-6 z-40 flex items-center justify-end gap-3 overflow-hidden',
              hasCover && 'text-white hover:text-white',
            )}
          >
            <ShareItineraryButton overImage={hasCover} />
            <Button
              variant={hasCover ? 'icon-glass' : 'icon'}
              size="icon"
              onClick={() => tripsState.toggleItineraryBuilderCollapsed()}
              tooltip="Collapse itinerary"
            >
              <MaterialIcon icon="right_panel_close" size="sm" />
            </Button>
          </div>

          <div className={cn('space-y-1', hasCover && '[text-shadow:0_1px_3px_rgb(0_0_0_/_0.2)]')}>
            <h2 className={cn('text-2xl font-display font-light', hasCover && 'text-white')}>{currentTrip?.title}</h2>

            <div className={cn('flex items-center gap-3', hasCover && 'text-white')}>
              <Button
                variant="link"
                className={hasCover ? 'text-white [text-shadow:0_1px_3px_rgb(0_0_0_/_0.2)]' : 'text-primary'}
                size="sm"
                onClick={() =>
                  tripsState.openDateDialog(currentTrip?.startDate ?? undefined, currentTrip?.endDate ?? undefined)
                }
              >
                <div className="truncate">
                  {(() => {
                    const start = currentTrip?.startDate;
                    const end = currentTrip?.endDate;
                    return start && end ? formatShortDateRange(start, end) : 'Select dates';
                  })()}
                </div>
              </Button>

              {nights > 0 && (
                <>
                  <span>&bull;</span>
                  <span className="text-sm font-medium">
                    {nights} {pluralize(nights, 'night')}
                  </span>
                </>
              )}

              {currentTrip?.isDemo && (
                <>
                  <span>&bull;</span>
                  <Badge variant="info" className="!text-shadow-none text-blue-800">
                    Demo
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex overflow-hidden">
          {/* Alpha mask: fades the top and bottom edges of the scroll area so content dissolves as it scrolls. */}
          <div className="h-full w-full flex flex-col gap-8 px-4 py-8 md:px-6 overflow-y-auto [mask-image:linear-gradient(to_bottom,transparent,black_2rem,black_calc(100%_-_2rem),transparent)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black_2rem,black_calc(100%_-_2rem),transparent)]">
            {!itineraryState.isFullyLoaded ? (
              <div className="flex flex-1 items-center justify-center py-12">
                <Loader loading={true} />
              </div>
            ) : (
              <>
                {canCollapse && (
                  <div className="flex justify-center">
                    <Button size="sm" onClick={() => setShowPastEvents((v) => !v)}>
                      <MaterialIcon icon={showPastEvents ? 'unfold_less' : 'history'} size="sm" />
                      {showPastEvents ? 'Hide past events' : 'See past events'}
                    </Button>
                  </div>
                )}
                {destinationGroups.map((group, groupIndex) => {
                  const prevGroup = groupIndex > 0 ? destinationGroups[groupIndex - 1] : undefined;
                  const dateRange = formatShortDateRange(group.startDateKey, group.endDateKey);
                  const destinationId = group.descriptor?.destinationId;
                  // Nights at this destination = one fewer than its (contiguous) days.
                  const groupNights = Math.max(0, group.days.length - 1);
                  // While collapsed, drop this group's past days; a fully-past group renders nothing.
                  const visibleDays = group.days.filter(
                    (day) => showPastEvents || !isPastDay(toLocalDateKey(day.date)),
                  );
                  if (visibleDays.length === 0) return null;
                  return (
                    <section
                      key={`${group.startDateKey}-${group.label}`}
                      className="flex flex-col gap-6 w-full max-w-140 mx-auto shadow-sm rounded-lg p-6 bg-background-secondary"
                    >
                      <div className="relative flex flex-col gap-0.5 text-xs">
                        <h3 className="text-lg font-display truncate">{group.label}</h3>
                        <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                          {dateRange && <span>{dateRange}</span>}
                          {groupNights > 0 && (
                            <>
                              <span>&bull;</span>
                              <span>
                                {groupNights} {pluralize(groupNights, 'night')}
                              </span>
                            </>
                          )}
                          {group.costs.length > 0 && (
                            <>
                              <span>&bull;</span>
                              <span className="flex-shrink-0">
                                {group.costs.map((c) => formatPrice(c.amount, c.currency)).join(' + ')}
                              </span>
                            </>
                          )}
                        </div>
                        {destinationId && (
                          <div className="absolute top-0 right-0">
                            <Button
                              variant="icon"
                              size="icon-sm"
                              tooltip={`${group.label} guide`}
                              onClick={() => tripsState.showGuide('destination', destinationId)}
                            >
                              <MaterialIcon icon="menu_book" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-6">
                        {group.showTransportationSection && (
                          <div className="flex flex-col gap-2">
                            <div className="text-xs font-medium text-muted-foreground">Choose your transportation</div>
                            <PlaceholderButton
                              label="Find flights"
                              icon="flight_takeoff"
                              onClick={() => {
                                chatInputState.setMessage(
                                  prevGroup
                                    ? `Find a flight from ${prevGroup.label} to ${group.label}.`
                                    : `Find a flight to ${group.label}.`,
                                );
                                void chatInputState.sendMessage();
                              }}
                            />
                          </div>
                        )}

                        {visibleDays.map((day) => (
                          <div key={day.dayNumber} className="flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-2 pb-1 text-muted-foreground text-xs">
                              <div className="font-medium">{day.formattedDate}</div>
                              {(() => {
                                // The hotel(s) the traveler is staying at this night (excludes the
                                // morning they check out). Shown in the day header rather than as a banner.
                                const stays = (hotelStaysByDay[format(day.date, 'yyyy-MM-dd')] || []).filter(
                                  (stay) => !stay.isCheckoutDay,
                                );
                                if (stays.length === 0) return null;
                                return (
                                  <div className="flex min-w-0 items-center gap-1.5">
                                    <MaterialIcon icon="hotel" size="xs" className="flex-shrink-0" />
                                    <span className="truncate">{stays.map((stay) => stay.hotelName).join(', ')}</span>
                                  </div>
                                );
                              })()}
                            </div>

                            {(() => {
                              const dateKey = format(day.date, 'yyyy-MM-dd');
                              const events = eventsByDay[dateKey] || [];

                              if (isTravelDay(eventsByDay, dateKey, format(subDays(day.date, 1), 'yyyy-MM-dd'))) {
                                return <div className="text-sm text-muted-foreground">Traveling</div>;
                              }

                              const { showActivitiesPlaceholder, showHotelPlaceholder, showDiningPlaceholder } =
                                day.placeholders;
                              const hasPlaceholders =
                                showActivitiesPlaceholder || showHotelPlaceholder || showDiningPlaceholder;

                              return (
                                <>
                                  {/* Events (hotel check-in rendered inline, checkout hidden) */}
                                  {events.map((event) => {
                                    if (event.type === 'hotel_checkout') return null;

                                    const eventModel = ItineraryItemEventModel.from(event);

                                    const items = resolveItemsForEvent(event, allItems);
                                    if (items.length === 0) return null;

                                    let content = (
                                      <BaseItineraryBlock
                                        event={eventModel}
                                        items={items}
                                        canDeleteItem={true}
                                        size="sm"
                                      />
                                    );

                                    switch (eventModel.type) {
                                      case 'hotel_checkin': {
                                        content = <HotelCheckinBlock event={eventModel} items={items} size="default" />;
                                        break;
                                      }
                                      case 'layover': {
                                        content = (
                                          <LayoverBlock
                                            event={eventModel}
                                            items={items}
                                            size="sm"
                                            className={cn('bg-background-tertiary text-muted-foreground pl-25 text-xs')}
                                          />
                                        );
                                        break;
                                      }
                                      case 'flight_departure': {
                                        content = (
                                          <FlightDepartureBlock event={eventModel} items={items} size="default" />
                                        );
                                        break;
                                      }
                                      case 'flight_arrival': {
                                        // Skip arrival blocks.
                                        return null;
                                      }
                                      case 'destination_arrival': {
                                        // Skip destination arrival blocks.
                                        return null;
                                      }
                                      case 'place':
                                      case 'place_dining':
                                      case 'place_shopping':
                                      case 'place_nature':
                                      case 'place_culture':
                                      case 'place_entertainment':
                                      case 'place_wellness':
                                      case 'place_nightlife': {
                                        content = <PlaceBlock event={eventModel} items={items} size="default" />;
                                        break;
                                      }
                                      case 'activity':
                                      case 'custom': {
                                        content = <CustomBlock event={eventModel} items={items} size="default" />;
                                        break;
                                      }
                                    }

                                    return (
                                      <div key={eventModel.id} className="w-full">
                                        {content}
                                      </div>
                                    );
                                  })}

                                  {hasPlaceholders && (
                                    <div
                                      className={cn(
                                        'grid grid-cols-1 gap-2',
                                        // `md:grid-cols-${placeholdersCount > 1 && 2}`,
                                      )}
                                    >
                                      {/* Contextual placeholders — shown whenever a category is missing,
                                    even on days that already have other events. */}
                                      {showHotelPlaceholder && (
                                        <PlaceholderButton
                                          label="Find places to stay"
                                          icon="hotel"
                                          onClick={() => {
                                            chatInputState.setMessage(`Find a hotel in ${group.label}.`);
                                            void chatInputState.sendMessage();
                                          }}
                                        />
                                      )}
                                      {showActivitiesPlaceholder && (
                                        <PlaceholderButton
                                          label="Find things to do"
                                          icon="local_activity"
                                          onClick={() => {
                                            chatInputState.setMessage(
                                              `Find things to do in ${group.label} on ${day.formattedDate}.`,
                                            );
                                            void chatInputState.sendMessage();
                                          }}
                                        />
                                      )}
                                      {showDiningPlaceholder && (
                                        <PlaceholderButton
                                          label="Find places to eat"
                                          icon="restaurant"
                                          onClick={() => {
                                            chatInputState.setMessage(
                                              `Find a restaurant in ${group.label} on ${day.formattedDate}.`,
                                            );
                                            void chatInputState.sendMessage();
                                          }}
                                        />
                                      )}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {currentTrip && (
          <BookTripModal
            open={bookModalOpen}
            onOpenChange={setBookModalOpen}
            tripId={currentTrip.id}
            items={allItems}
          />
        )}

        {/* Footer */}
        {itineraryState.isFullyLoaded && (
          <div className="w-full p-6 pt-2 z-30 flex items-center gap-6 justify-between bg-gradient-to-t from-background-tertiary via-background-tertiary to-transparent">
            <div className="space-y-0.5">
              <div className="text-xs font-medium text-muted-foreground">Estimated total</div>
              {hasTotals ? (
                <CostTotals
                  totalsByCurrency={totalCostsByCurrency}
                  className="flex flex-wrap items-baseline gap-1.5 text-lg font-medium"
                />
              ) : (
                <span className="text-lg font-medium">N/A</span>
              )}
            </div>
            <div className="flex-shrink-0">
              {itemsToBookCount > 0 && (
                <Button size="lg" onClick={() => setBookModalOpen(true)}>
                  {itemsToBookCount} {pluralize(itemsToBookCount, 'item')} to book
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
